import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { ApiService } from "../../../../Services/ApiService";
import {
  getLocalStorageItem,
} from "../../../../Services/Global";

// Extracted modules
import { 
  BlindTestDashboardProps, 
  useBlindTestDashboardState 
} from './BlindTestDashboardTypes';
import { 
  createBlindTestDashboardStyles, 
  createBlindTestDashboardTheme 
} from './BlindTestDashboardStyles';
import {
  NavigationSidebar,
  PositionSummaryCard,
  ContentDisplay,
  PerformanceMetricsDisplay
} from './BlindTestDashboardComponents';
import {
  createNavigationHandlers,
  createCardHandlers,
  createDownloadHandlers,
  createErrorHandlers,
  createInitializationHandlers
} from './BlindTestDashboardEventHandlers';
import {
  createBlindTestDashboardApiOperations
} from './BlindTestDashboardApi';
import {
  createRenderCards
} from './BlindTestDashboardUtils';

/**
 * BlindTestDashboard component to display the blind test dashboard for SME and Modeler users
 */
export const BlindTestDashboard: React.FC<BlindTestDashboardProps> = ({
  isModeler,
  isSme,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const ctx = useMsal();
  const apiService = useMemo(() => new ApiService(ctx), [ctx]);
  
  // Use the extracted state management hook
  const state = useBlindTestDashboardState();
  
  // Memoize styles and theme to prevent recreation on every render
  const styles = useMemo(() => createBlindTestDashboardStyles(theme), [theme]);
  const customTheme = useMemo(() => createBlindTestDashboardTheme(), []);

  // Create API operations
  const apiOperations = useMemo(() => 
    createBlindTestDashboardApiOperations(apiService, state.logPerformance), 
    [apiService]
  );

  // Create handlers with minimal dependencies to prevent loops
  const navigationHandlers = useMemo(() => 
    createNavigationHandlers(
      state.setExpandedItems,
      state.setSelectedIndustry,
      state.setSelectedSubIndustry,
      state.setSelectedApplication,
      state.setSelectedSubApplication,
      state.setSelectedKey,
      state.setDisplaySummaries,
      state.setActiveCardId
    ), 
    []
  );

  const cardHandlers = useMemo(() => 
    createCardHandlers(
      isModeler,
      isSme,
      navigate,
      state.setActiveCardId
    ), 
    [isModeler, isSme, navigate]
  );

  const downloadHandlers = useMemo(() => 
    createDownloadHandlers(
      state.setDownloadingItems,
      state.setError,
      state.setShowError
    ), 
    []
  );

  const errorHandlers = useMemo(() => 
    createErrorHandlers(
      state.setError,
      state.setShowError
    ), 
    []
  );

  const initializationHandlers = useMemo(() => 
    createInitializationHandlers(
      state.setSelectedIndustry,
      state.setSelectedSubIndustry,
      state.setSelectedApplication,
      state.setSelectedSubApplication,
      state.setDisplaySummaries
    ), 
    []
  );

  // API wrapper functions with minimal dependencies
  const getPositionSummariesWrapped = useCallback(async (
    industry: string,
    subIndustry: string,
    application: string,
    subApplication: string
  ) => {
    await apiOperations.getPositionDesignSessionSummary(
      industry,
      subIndustry,
      application,
      subApplication,
      state.setIsLoading,
      state.setError,
      state.setShowError,
      state.setActiveCardId,
      state.setPositionSummaries
    );
  }, [apiOperations]);

  const downloadCSVFileWrapped = useCallback(async (datasetId: string, item: any) => {
    await apiOperations.downloadCSVFile(
      datasetId,
      item,
      downloadHandlers.setCardDownloading,
      state.setError,
      state.setShowError
    );
  }, [apiOperations, downloadHandlers]);

  // Set active card when data is loaded
  useEffect(() => {
    if (state.positionSummaries.length > 0 && !state.activeCardId) {
      const defaultCard =
        state.positionSummaries.find((item) => item.summary["todo"] > 0) || 
        state.positionSummaries[0];
        
      if (defaultCard) {
        const cardId = `${defaultCard.alfaLavalPosition}-${defaultCard.productLine}-${defaultCard.blindTestDataSetId}`;
        state.setActiveCardId(cardId);
      }
    }
  }, [state.positionSummaries.length, state.activeCardId]);

  // Load position summary when selection changes. All four selection values
  // must be listed so the effect reacts to any of them changing, not just
  // `selectedSubApplication`; this prevents reading stale industry /
  // sub-industry / application closures if any of them mutate independently.
  useEffect(() => {
    if (
      state.selectedIndustry &&
      state.selectedSubIndustry &&
      state.selectedApplication &&
      state.selectedSubApplication
    ) {
      getPositionSummariesWrapped(
        state.selectedIndustry,
        state.selectedSubIndustry,
        state.selectedApplication,
        state.selectedSubApplication
      );
    }
  }, [
    state.selectedIndustry,
    state.selectedSubIndustry,
    state.selectedApplication,
    state.selectedSubApplication,
    getPositionSummariesWrapped,
  ]);

  // `loadNavigationLinks` reads `displaySummaries` once to decide whether to
  // clear the loading flag. Keep that semantics (mount-only call) while
  // guaranteeing it always observes the latest value – avoids stale-closure
  // risk if the initialization sequence ever changes.
  const displaySummariesRef = useRef(state.displaySummaries);
  useEffect(() => {
    displaySummariesRef.current = state.displaySummaries;
  }, [state.displaySummaries]);

  // Initialize from local storage and load navigation - run only once
  useEffect(() => {
    initializationHandlers.initializeFromLocalStorage(getLocalStorageItem);

    apiOperations.loadNavigationLinks(
      state.setError,
      state.setIsLoading,
      state.setShowError,
      state.setNavItems,
      displaySummariesRef.current
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create render cards function
  const renderCards = useMemo(() => 
    createRenderCards(
      state.positionSummaries,
      state.activeCardId,
      state.downloadingItems,
    isModeler,
    isSme,
      cardHandlers.handleCardClick,
      downloadCSVFileWrapped,
    styles,
      PositionSummaryCard
    ), 
    [
      state.positionSummaries,
      state.activeCardId,
      state.downloadingItems,
      isModeler,
      isSme,
      cardHandlers,
      downloadCSVFileWrapped,
      styles
    ]
  );

  return (
    <ThemeProvider theme={customTheme}>
      <PerformanceMetricsDisplay performanceMetrics={state.performanceMetrics} />
      
      <Stack
        direction="row"
        spacing={2}
        sx={styles.mainContainer}
      >
        <Box sx={styles.mainSidebar}>
          <NavigationSidebar
            navItems={state.navItems}
            isLoading={state.isLoading && !state.positionSummaries.length}
            expandedItems={state.expandedItems}
            selectedPaths={{
              industry: state.selectedIndustry,
              subIndustry: state.selectedSubIndustry,
              application: state.selectedApplication,
              subApplication: state.selectedSubApplication,
            }}
            onToggleItem={navigationHandlers.handleToggleItem}
            onSubApplicationClick={navigationHandlers.handleSubApplicationClick}
            styles={styles}
          />
        </Box>

        <ContentDisplay
          isLoading={state.isLoading}
          displaySummaries={state.displaySummaries}
          selectedSubApplication={state.selectedSubApplication}
          positionSummaries={state.positionSummaries}
          renderCards={renderCards}
          styles={styles}
        />

        <Snackbar
          open={state.showError}
          autoHideDuration={6000}
          onClose={errorHandlers.handleCloseError}
          anchorOrigin={styles.snackbar.anchorOrigin}
        >
          <Alert onClose={errorHandlers.handleCloseError} severity="error" variant="filled">
            {state.error}
          </Alert>
        </Snackbar>
      </Stack>
    </ThemeProvider>
  );
};
